import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import HomeIcon from '@mui/icons-material/Home';
import { ROOT_FOLDER_ID } from '../utils/constants';
import { getDescendantFolderIds } from '../utils/workspace';
import { useWorkspace } from '../store/WorkspaceContext';

function FolderTreeRow({ folder, level, selectedId, disabledIds, childrenByParent, onSelect }) {
  const disabled = disabledIds.has(folder.id);
  const children = childrenByParent.get(folder.id) || [];
  const selected = selectedId === folder.id;

  return (
    <>
      <ListItemButton
        selected={selected}
        disabled={disabled}
        onClick={() => onSelect(folder.id)}
        sx={{ pl: 2 + level * 2, borderRadius: 1, mb: 0.25 }}
      >
        <ListItemIcon sx={{ minWidth: 36 }}>
          {folder.id === ROOT_FOLDER_ID ? <HomeIcon /> : <FolderIcon />}
        </ListItemIcon>
        <ListItemText
          primary={folder.name}
          secondary={disabled ? 'Unavailable destination' : null}
          primaryTypographyProps={{ noWrap: true }}
        />
      </ListItemButton>
      {children.map((child) => (
        <FolderTreeRow
          key={child.id}
          folder={child}
          level={level + 1}
          selectedId={selectedId}
          disabledIds={disabledIds}
          childrenByParent={childrenByParent}
          onSelect={onSelect}
        />
      ))}
    </>
  );
}

export default function MoveDialog({
  open,
  item,
  itemType,
  currentParentId,
  initialSelectedId,
  disabledIds,
  title,
  onClose,
  onMove,
}) {
  const { folders } = useWorkspace();
  const [selectedId, setSelectedId] = useState(ROOT_FOLDER_ID);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setSelectedId(initialSelectedId || currentParentId || ROOT_FOLDER_ID);
      setError('');
    }
  }, [currentParentId, initialSelectedId, open]);

  const childrenByParent = useMemo(() => {
    const map = new Map();

    folders.forEach((folder) => {
      const parentId = folder.parentId || 'none';
      const children = map.get(parentId) || [];
      children.push(folder);
      map.set(parentId, children);
    });

    map.forEach((children) => children.sort((a, b) => a.name.localeCompare(b.name)));
    return map;
  }, [folders]);

  const computedDisabledIds = useMemo(() => {
    if (disabledIds instanceof Set) {
      return disabledIds;
    }

    if (Array.isArray(disabledIds)) {
      return new Set(disabledIds);
    }

    if (!item || itemType !== 'folder') {
      return new Set();
    }

    return new Set([item.id, ...getDescendantFolderIds(folders, item.id)]);
  }, [disabledIds, folders, item, itemType]);

  const rootFolder = folders.find((folder) => folder.id === ROOT_FOLDER_ID);

  const handleMove = () => {
    try {
      onMove(selectedId);
      onClose();
    } catch (moveError) {
      setError(moveError.message || 'Unable to move this item.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title || `Move ${item?.name || 'Item'}`}</DialogTitle>
      <DialogContent>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}
        <Box
          sx={{
            maxHeight: 420,
            overflow: 'auto',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            p: 1,
          }}
        >
          <List disablePadding>
            {rootFolder ? (
              <FolderTreeRow
                folder={rootFolder}
                level={0}
                selectedId={selectedId}
                disabledIds={computedDisabledIds}
                childrenByParent={childrenByParent}
                onSelect={setSelectedId}
              />
            ) : null}
          </List>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleMove}
          disabled={!selectedId || computedDisabledIds.has(selectedId)}
        >
          Move
        </Button>
      </DialogActions>
    </Dialog>
  );
}
